import { isPlainObject } from 'is-plain-object';
import { apiGetKnockStatus } from './api';
import { Participant, Room } from 'twilio-video';

export const isMobile = (() => {
  if (typeof navigator === 'undefined' || typeof navigator.userAgent !== 'string') {
    return false;
  }
  return /Mobile/.test(navigator.userAgent);
})();

// Recursively removes any object keys with a value of undefined
export function removeUndefineds<T>(obj: T): T {
  if (!isPlainObject(obj)) return obj;

  const target: { [name: string]: any } = {};

  for (const key in obj) {
    const val = obj[key];
    if (typeof val !== 'undefined') {
      target[key] = removeUndefineds(val);
    }
  }

  return target as T;
}

export async function getDeviceInfo() {
  const devices = await navigator.mediaDevices.enumerateDevices();

  return {
    audioInputDevices: devices.filter(device => device.kind === 'audioinput'),
    videoInputDevices: devices.filter(device => device.kind === 'videoinput'),
    audioOutputDevices: devices.filter(device => device.kind === 'audiooutput'),
    hasAudioInputDevices: devices.some(device => device.kind === 'audioinput'),
    hasVideoInputDevices: devices.some(device => device.kind === 'videoinput'),
  };
}

// This function will return 'true' when the specified permission has been denied by the user.
// If the API doesn't exist, or the query function returns an error, 'false' will be returned.
export async function isPermissionDenied(name: 'camera' | 'microphone') {
  const permissionName = name as PermissionName; // workaround for https://github.com/microsoft/TypeScript/issues/33923

  if (navigator.permissions) {
    try {
      const result = await navigator.permissions.query({ name: permissionName });
      return result.state === 'denied';
    } catch {
      return false;
    }
  } else {
    return false;
  }
}

/**
 * This function capitalises the first letter of the given word and makes the rest lowercase
 * @param word string | null
 * @returns string
 */
export const capitalize = (word: string | null): string => {
  if (!word) return '';
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

/**
 * This function creates the roomName using the user's username and domain
 * @param username string
 * @param domain string
 * @returns string
 */
export const createRoomName = (username: string, domain: string) => {
  let alphanumericRegex = new RegExp('[^0-9a-z]', 'gi');
  username = username.replace(alphanumericRegex, '');
  domain = domain.replace(alphanumericRegex, '');
  return capitalize(username) + capitalize(domain) + 'Meet';
};

/**
 * Extracts and returns the username from the roomname
 * @param roomname string
 * @param domain string
 * @returns string
 */
export const getUsernameFromRoomname = (roomname: string, domain: string) => {
  const regex = new RegExp(`${domain}|meet`, 'ig');
  return roomname.replace(regex, '');
};

/**
 * This function capitalises the first letter of the given word and does not change the rest letters
 * @param word string | null
 * @returns string
 */
export const capitalizeFirst = (word: string | null): string => {
  if (!word) return '';
  return word.charAt(0).toUpperCase() + word.slice(1);
};

/**
 * Generates a random id of the given length
 * @param length number
 * @returns
 */
export const generateRandomId = (length: number) => {
  let chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ!@#$%^&*()_';
  let result = '';
  for (let i = length; i > 0; --i) result += chars[Math.floor(Math.random() * chars.length)];
  return result;
};

/**
 * Returns the UPN from the given username and domain
 * @param username string
 * @param domain string
 * @returns string
 */
export const getUpn = (username: string, domain: string) => {
  return `${username.toLowerCase()}@${domain.toLowerCase()}.local`;
};

/**
 * Checks the knock status of the user in the host room
 * @param username string
 * @param hostUpn string - UPN of the host
 * @returns 0 | 1 | -1 (0: accepted, 1: rejected, -1: pending)
 */
export const getKnockStatus = async (username: string, hostUpn: string): Promise<0 | 1 | -1> => {
  const data = await apiGetKnockStatus(hostUpn);
  if (data.length) {
    for (let i = 0; i < data.length; i++) {
      const arr = data[i].split(',');
      if (arr[0] === username) {
        return +arr[1] as 0 | 1;
      }
    }
  }
  return -1;
};

/**
 * Checks if the user is the owner of the room
 * @param room Room
 * @returns boolean
 */
export const isRoomOwner = (room: Room, participant?: Participant) => {
  const identity = participant ? participant.identity : room.localParticipant.identity;
  return room.name.toLocaleLowerCase().includes(removeNonAplhanumericChars(identity).toLocaleLowerCase());
};

/**
 * Function to remove all non-alphanumeric characters from the given string
 * @param str string
 * @returns string
 */
export const removeNonAplhanumericChars = (str: string) => {
  let alphanumericRegex = new RegExp('[^0-9a-z]', 'gi');
  return str.replace(alphanumericRegex, '');
};

/**
 * Function to get the join url for the room
 */
export const getJoinUrl = (roomName: string, domain: string): string => {
  return `${window.location.origin}/video?room=${roomName}${domain && `&domain=${domain}`}`;
};

/**
 * Formats a number into a correct format with country code
 * @param phoneNumber
 * @returns
 */
export const formatNumber = (phoneNumber: string | number) => {
  if (typeof phoneNumber === 'number') {
    phoneNumber = phoneNumber.toString();
  }
  return phoneNumber.length === 10 ? `+1${phoneNumber}` : `+${phoneNumber}`;
};

export function xmlToJson(xmlString: string) {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
  const json: any = {};
  const nodes = xmlDoc.documentElement.childNodes;
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (node.nodeType === 1) {
      if (node.hasChildNodes() && node.firstChild?.nodeType === 1) {
        if (!json[node.nodeName]) {
          // @ts-ignore
          json[node.nodeName] = xmlToJson(node.outerHTML);
        } else if (Array.isArray(json[node.nodeName])) {
          // @ts-ignore
          json[node.nodeName].push(xmlToJson(node.outerHTML));
        } else {
          const prevValue = json[node.nodeName];
          // @ts-ignore
          json[node.nodeName] = [prevValue, xmlToJson(node.outerHTML)];
        }
      } else {
        if (json[node.nodeName]) {
          if (Array.isArray(json[node.nodeName])) {
            json[node.nodeName].push(node.textContent);
          } else {
            const prevValue = json[node.nodeName];
            json[node.nodeName] = [prevValue, node.textContent];
          }
        } else {
          json[node.nodeName] = node.textContent;
        }
      }
    }
  }
  return json;
}
